import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IMessageText, Message } from '../models/Message';
import { environment as ENV } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AppService } from './app.service';

const storage: {
    texts: { [language: string]: { [code: string]: IMessageText } }
} = {
    texts: {}
};

@Injectable({ providedIn: "root" })
export class MessageService {
    constructor(private http: HttpClient, private app: AppService) { }

    private readonly apiUrl = `${ENV.apiUrl}/messageTemplates`;

    get(filter?: {
        group?: string,
        code?: string,
        title?: string,
        textEn?: string,
        textLv?: string
    }): Observable<Message[]> {
        const params: any = {...filter};
        return this.http.get<Message[]>(this.apiUrl, { params });
	}

    getText(code: string | string[], language?: string, cache: boolean = true): Observable<IMessageText[]> {
        let codes: string[] = [];

        if (code) {
            if (typeof code === 'string') {
                codes = code.split(',');
            } else {
                codes = code;
            }
        }

        if (!language) {
            language = this.app.currentLanguage;
        }

        if (!storage.texts[language]) {
            storage.texts[language] = {};
        }

        const langStorage = storage.texts[language];

        if (!cache || codes.some(t => !langStorage[t])) {
            const url = `${this.apiUrl}/text`;

            const params: any = {
                code: codes.join(','),
                language
            };

            let req = this.http.get<IMessageText[]>(url, { params });

            if (cache) {
                req = req.pipe(tap(data => {
                    data.forEach(t => {
                        langStorage[t.Code] = t;
                    });
                }));
            }

            return req;
        } else {
            const items = codes.reduce((all, t) => {
                all.push(langStorage[t]);
                return all;
            }, [] as IMessageText[]);

            return of(items);
        }
    }

	getById(id: number): Observable<Message> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<Message>(url);
	}

    create(message: Message): Observable<Message> {
        return this.http.post<Message>(this.apiUrl, message).pipe(tap(() => {
            storage.texts = {};
        }));
    }

    update(id: number, message: Message): Observable<Message> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put<Message>(url, message).pipe(tap(() => {
            storage.texts = {};
        }));
    }

    delete(id: number): Observable<any> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.delete<any>(url).pipe(tap(() => {
            storage.texts = {};
        }));
    }
}
